// src/LastActionStatus.js
import React, { useState, useEffect } from "react";
import "./LastActionStatus.css"; // Ensure CSS is updated
import logger from './utils/logger';

// --- Helper rendering functions ---
const renderActionStatus = (action) => {
  if (!action || !action.actionStatus) return null;

  if (action.isTimedOut && action.actionStatus.toLowerCase() === "pending") {
    return <span className="status-timeout">60-SEC TIMEOUT</span>;
  }

  const lowerCaseStatus = action.actionStatus.toLowerCase();
  let className = "status-pending"; // Default color
  if (lowerCaseStatus === "resolved") {
    className = "status-resolved";
  } else if (lowerCaseStatus === "rejected") {
    className = "status-rejected";
  }
  return <span className={className}>{action.actionStatus}</span>;
};

const calculateResolutionTime = (action) => {
  if (action.isTimedOut && action.actionStatus.toLowerCase() === "pending") {
    return <span className="emoji-large">😅</span>;
  }
  // Show spinner if update timestamp is missing
  if (!action.lastUpdatedAt || !action.createdAt) {
    return <span className="spinner"></span>;
  }
  try {
    const start = new Date(action.createdAt);
    const end = new Date(action.lastUpdatedAt);
    // Show spinner if dates are invalid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return <span className="spinner"></span>;
    }
    const diff = (end.getTime() - start.getTime()) / 1000;
    // Ensure time is not negative
    return `${Math.max(0, diff).toFixed(3)} seconds`;
  } catch (e) {
    logger.error("Error calculating resolution time:", e, {
      createdAt: action.createdAt,
      lastUpdatedAt: action.lastUpdatedAt,
    });
    return "Invalid Date";
  }
};

const calculateCloudReactionTime = (action) => {
  if (action.isTimedOut && action.actionStatus.toLowerCase() === "pending") {
    return <span className="emoji-large">😅</span>;
  }
  // Show spinner if update timestamp is missing
  if (!action.cloudUpdatedAt || !action.cloudCreatedAt) {
    return <span className="spinner"></span>;
  }
  try {
    const start = new Date(action.cloudCreatedAt); // Start time
    const end = new Date(action.cloudUpdatedAt); // End time
    // Show spinner if dates are invalid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return <span className="spinner"></span>;
    }
    const diff = (end.getTime() - start.getTime()) / 1000;
    // Ensure time is not negative
    return `${Math.max(0, diff).toFixed(3)} seconds`;
  } catch (e) {
    logger.error("Error calculating device to cloud reaction time:", e, {
      cloudCreatedAt: action.cloudCreatedAt,
      cloudUpdatedAt: action.cloudUpdatedAt,
    });
    return "Invalid Date";
  }
};
// --- End Helper Functions ---

// --- Component Definition ---
const LastActionStatus = ({
  actionHistory,
  limit, // Current limit value from parent (App.js)
  onLimitChange, // Function to update limit in parent
  isCollapsed,
  onToggleCollapse,
  dragHandleProps, // Drag handle props for the header
}) => {
  const [inputValue, setInputValue] = useState(limit.toString());
  const [copiedActionId, setCopiedActionId] = useState(null);

  const handleCopy = (text, actionId) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopiedActionId(actionId);
        setTimeout(() => setCopiedActionId(null), 2000);
      },
      (err) => {
        logger.error("Failed to copy: ", err);
      }
    );
  };

  useEffect(() => {
    setInputValue(limit.toString());
  }, [limit]);

  const handleLimitChange = (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    if (newValue === "") {
      return;
    }
    const parsedValue = parseInt(newValue, 10);
    if (!isNaN(parsedValue) && parsedValue >= 1 && parsedValue <= 30) {
      onLimitChange(parsedValue);
    }
  };

  const handleBlur = () => {
    let finalLimit = parseInt(inputValue, 10);
    if (inputValue === "" || isNaN(finalLimit) || finalLimit < 1) {
      finalLimit = 1;
    } else if (finalLimit > 30) {
      finalLimit = 30;
    }
    setInputValue(finalLimit.toString());
    if (finalLimit !== limit) {
      onLimitChange(finalLimit);
    }
  };

  const displayedHistory = actionHistory.slice(0, limit);
  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="last-action-container">
      <div
        className="last-action-header"
        onClick={onToggleCollapse}
        {...dragHandleProps}
      >
        <h2 className="header-title-area">
          <span>
            Last Action Status History
            <span className="info-tooltip">
              <span className="info-icon">ⓘ</span>
              <span className="tooltip-text">
                When loading or refreshing the app, note that the Last Action
                Status only stores the last event. This is by design for demo
                purposes. To see more events in the list, simply run more
                events. To see more events than the default of 5, change the
                value in the Last X events control.
              </span>
            </span>
          </span>
        </h2>

        {!isCollapsed && (
          <div
            className="action-history-controls header-controls-area"
            onClick={stopPropagation}
          >
            <label htmlFor="history-limit">Show Last:</label>
            <input
              id="history-limit"
              type="number"
              min="1"
              max="30"
              value={inputValue}
              onChange={handleLimitChange}
              onBlur={handleBlur}
              className="history-limit-input"
            />
            <span>Events</span>
          </div>
        )}
        <span className="collapse-icon header-collapse-icon-area">
          {isCollapsed ? "▼" : "▲"}
        </span>
      </div>

      {!isCollapsed && (
        <div className="action-history-list">
          {displayedHistory.length > 0 ? (
            <div className="action-grid">
              {/* Grid Header */}
              <div className="action-label">Action Name:</div>
              <div className="action-label">Action ID:</div>
              <div className="action-label">Action Status:</div>
              <div className="action-label">Created At:</div>
              <div className="action-label">Updated At:</div>
              <div className="action-label">Roundtrip Cloud Time:</div>
              <div className="action-label">Device Reaction Time:</div>

              {/* Grid Rows */}
              {displayedHistory.map((action) => (
                <React.Fragment key={`${action.actionId}-${action.createdAt}`}>
                  <div className="action-value">
                    {action.actionName || "N/A"}
                  </div>
                  <div className="action-value">
                    {action.actionId || "N/A"}
                    {action.actionId && (
                      <button
                        className="copy-icon-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(action.actionId, action.actionId);
                        }}
                        title="Copy Action ID"
                        aria-label="Copy Action ID"
                      >
                        {copiedActionId === action.actionId ? "✅" : "📋"}
                      </button>
                    )}
                  </div>
                  <div className="action-value">
                    {renderActionStatus(action)}
                  </div>
                  <div className="action-value">
                    {action.createdAt
                      ? new Date(action.createdAt).toLocaleString()
                      : "N/A"}
                  </div>
                  <div className="action-value">
                    {action.lastUpdatedAt ? (
                      new Date(action.lastUpdatedAt).toLocaleString()
                    ) : (
                      <span className="spinner" />
                    )}
                  </div>
                  <div className="action-value">
                    {calculateResolutionTime(action)}
                  </div>
                  <div className="action-value cloud-reaction-time">
                    {calculateCloudReactionTime(action)}
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p className="no-actions-message">
              No action history available for this device.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LastActionStatus;
